import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@uw-env/environment";
import { filter } from "lodash";
import { Observable, tap, map } from 'rxjs';
import { CampaignListItemResponse, CampaignListWithUnitedWayResponse, CatalogConfigurationResponse, CatalogDetailResponse, CatalogResponse, CatalogTypeResponse, CreateCatalogConfigurationRequest, CreateUpdateSUWsRequest, CreateUpdateSUWsResponse, UnitedWayResponse, UpdateCatalogConfigurationRequest } from "../api/models";

@Injectable({
  providedIn: "root"
})
export class CatalogMgmtService {
  constructor(
    private readonly _client: HttpClient
  ) { }

  getCatalogTypes(): Observable<CatalogTypeResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalogType/list`;
    return this._client.get<{ data: CatalogTypeResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  getCatalogs(): Observable<CatalogResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalog/list`;
    return this._client.get<{ data: CatalogResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  getCatalogsByCatalogType(catalogTypeCode: string): Observable<CatalogResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalog/list/${catalogTypeCode}`;
    return this._client.get<{ data: CatalogResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  deleteConfiguration(catalogId: number): Observable<boolean> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalogconfig/${catalogId}`;
    return this._client.delete<{ data: any }>(url).pipe(
      map(res => res.data)
    )
  }

  createConfiguration(request: CreateCatalogConfigurationRequest): Observable<CatalogConfigurationResponse> {
    const url = `${environment.apiURL}/administration/catalogMgmt/create/catalogconfig`;
    return this._client.post<{ data: CatalogConfigurationResponse }>(url, request).pipe(
      map(res => res.data)
    )
  }

  updateConfiguration(request: UpdateCatalogConfigurationRequest): Observable<CatalogConfigurationResponse> {
    const url = `${environment.apiURL}/administration/catalogMgmt/update/catalogconfig`;
    return this._client.post<{ data: CatalogConfigurationResponse }>(url, request).pipe(
      map(res => res.data)
    )
  }

  catalogSearch(searchText: string, userId: number, catalogTypeCode: string): Observable<CatalogDetailResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalog/search/${searchText}/${catalogTypeCode}`;
    return this._client.get<{ data: CatalogDetailResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  craCatalogAutoCompleteSearch(searchText: string, userId: number, catalogTypeCode: string, catelogId: number): Observable<CatalogDetailResponse | null> {
    const url = `${environment.apiURL}/administration/catalogMgmt/catalog/search/${searchText}/${catalogTypeCode}`;
    return this._client.get<{ data: CatalogDetailResponse[] }>(url).pipe(
      map(res => res.data),
      map(result => result.filter(c => c.catalogId == catelogId)),
      map(filterResult => filterResult ? filterResult[0] : null)
    )
  }

  createCatalog(request: any): Observable<CatalogResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/create/catalog`;
    return this._client.post<{ data: CatalogResponse[] }>(url, request).pipe(
      map(res => res.data)
    )
  }

  getChartiryCRANumber(serachTerm: string): Observable<string[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/craSearch/${serachTerm}`;
    return this._client.get<{ data: { charitableRegistrationNumber: string }[] }>(url).pipe(
      map(res => res.data)
      , map((drcs: { charitableRegistrationNumber: string }[]) => drcs.map(c => c.charitableRegistrationNumber))
    );
  }

  createCatalogItem(request: any): Observable<CatalogDetailResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/create/catalogItem`;
    return this._client.post<{ data: CatalogDetailResponse[] }>(url, request).pipe(
      map(res => res.data)
    )
  }

  update(request: any[]): Observable<CatalogDetailResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/update`;
    return this._client.post<{ data: CatalogDetailResponse[] }>(url, request).pipe(
      map(res => res.data)
    )
  }

  getSuws(userId: number, campaignId: string | number): Observable<CreateUpdateSUWsResponse[]> {
    const url = campaignId == '0' || campaignId == 0
          ? `${environment.apiURL}/administration/catalogMgmt/suw/list`
          : `${environment.apiURL}/administration/catalogMgmt/suw/list?campaignId=${campaignId}`
    return this._client.get<{ data: CreateUpdateSUWsResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  createSuw(request: CreateUpdateSUWsRequest): Observable<CreateUpdateSUWsResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/create/suw`;
    const dataToSend = {
      ...request,
      cat2Id: request.cat2Id && request.cat2Id as unknown != 'null' ? request.cat2Id : null,
      cfId: request.cfId && request.cfId as unknown != 'null' ? request.cfId : null,
      saId: request.saId && request.saId as unknown != 'null' ? request.saId : null,
    }
    return this._client.post<{ data: CreateUpdateSUWsResponse[] }>(url, dataToSend).pipe(
      map(res => res.data)
    )
  }

  updateSuw(request: CreateUpdateSUWsRequest): Observable<CreateUpdateSUWsResponse[]> {
    const url = `${environment.apiURL}/administration/catalogMgmt/update/suw`;
    const dataToSend = {
      ...request,
      cat2Id: request.cat2Id && request.cat2Id as unknown != 'null' ? request.cat2Id : null,
      cfId: request.cfId && request.cfId as unknown != 'null' ? request.cfId : null,
      saId: request.saId && request.saId as unknown != 'null' ? request.saId : null,
    }
    return this._client.post<{ data: CreateUpdateSUWsResponse[] }>(url, dataToSend).pipe(
      map(res => res.data)
    )
  }

  getCampaigns() {
    const url = `${environment.apiURL}/administration/catalogMgmt/Campaign/List`;
    return this._client.get<{ data: CampaignListWithUnitedWayResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }

  getUnitedWays() {
    const url = `${environment.apiURL}/administration/admin/unitedway/list`;
    return this._client.get<{ data: UnitedWayResponse[] }>(url).pipe(
      map(res => res.data)
    )
  }
}
